import { create } from 'zustand';

// 网站配置接口
interface WebsiteConfig {
    // 导航配置
    navigation: {
        mainNav: Array<{
            id: string;
            title: string;
            path: string;
            subNav?: Array<{
                id: string;
                title: string;
                path: string;
            }>;
        }>;
    };

    // 个人信息
    personalInfo: {
        name: string;
        title: string;
        description: string;
        avatar: string;
    };

    // 教育背景
    education: {
        title: string;
        description: string;
        experiences: Array<{
            period: string;
            school: string;
            degree: string;
            major: string;
            gpa?: string;
            ranking?: string; // 排名信息
            honors?: string[]; // 荣誉奖项
            papers?: string[]; // 论文发表
            description?: string; // 学习经历描述
            learningPoints?: string[]; // 学习要点列表
            relevance?: string; // 相关课程
        }>;
        courses?: Array<{
            name: string;
            description: string;
            relevance: string;
        }>;
        awards: Array<{
            name: string;
            year: string;
            description: string;
        }>;
    };

    // 实习经历
    internships: Array<{
        id: string;
        company: string;
        position: string;
        period: string;
        location: string;
        logo: string;
        description: string;
        workContent: { [key: string]: string[] }; // 工作内容，键为成就标题，值为职责列表
        skills: string[];
    }>;

    // 个人项目
    projects: Array<{
        id: string;
        title: string;
        description: string;
        slogan: string; // 项目标语，用于首页展示
        image: string;
        features: string[];
        implementationPath?: string; // 实现路径
        demoUrl?: string;
        githubUrl?: string;
    }>;

    // 书籍读后感
    bookReviews: Array<{
        title: string;
        author: string;
        cover: string;
        rating: number;
        review: string;
        keyInsights: string[];
    }>;

    // 简历文件
    resume: {
        chinese: string;
        english: string;
    };

    // Footer配置
    footer: {
        statement: string;
        companyLogos: Array<{
            name: string;
            logo: string;
            url?: string;
        }>;
        contact: {
            email: string;
            linkedin?: string;
            github?: string;
        };
    };
}

// 默认配置
const defaultConfig: WebsiteConfig = {
    navigation: {
        mainNav: [
            {
                id: 'projects',
                title: '项目',
                path: '/projects',
                subNav: [
                    { id: 'thinker-model', title: '思想家大模型', path: '/projects/thinker-model' },
                    { id: 'tech-policy', title: '科技政策平台', path: '/projects/tech-policy' },
                    // { id: 'info213', title: 'info213语言交换平台', path: '/projects/info213' },
                    { id: 'barrage-analysis', title: '弹幕分析', path: '/projects/barrage-analysis' }
                ]
            },
            {
                id: 'internships',
                title: '实习',
                path: '/internships',
                subNav: [
                    { id: 'meituan', title: '美团-用户产品', path: '/internships/meituan' },
                    { id: 'xiaomi', title: '小米-策略产品', path: '/internships/xiaomi' },
                    { id: 'xueda', title: '学大-b端产品', path: '/internships/xueda' }
                ]
            },
            {
                id: 'resume',
                title: '简历',
                path: '/resume'
            },
            {
                id: 'qa',
                title: '答疑专区',
                path: '/qa'
            }
        ]
    },

    personalInfo: {
        name: '张冉',
        title: '产品经理',
        description: '链接技术与用户体验，专注打造未来 AI 驱动的产品价值。',
        avatar: '/images/avatar.jpg'
    },

    education: {
        title: '教育背景',
        description: '通过系统性的学习，我建立了扎实的理论基础和实践能力，为职业发展奠定了坚实基础。',
        experiences: [
            {
                period: '2020-2024',
                school: '南京农业大学',
                degree: '本科',
                major: '信息管理与信息系统',
                // gpa: '3.8/4.0',
                ranking: '1/65',
                honors: ['国家奖学金', '南京N6辩论赛亚军', '江苏省计算机设计大赛二等奖', '"挑战杯"江苏省大学生创业计划竞赛铜奖'],
                // description: '主修信息管理与信息系统，辅修经济学，培养了技术思维和商业洞察力',
                relevance: '信息系统分析与设计、信息调查方法、信息行为分析、信息检索系统、大数据基础、数据库系统、Python'
            },
            {
                period: '2024-2027',
                school: '南京大学',
                degree: '硕士',
                major: '图书情报',
                // gpa: '3.9/4.0',
                // ranking: '2/30',
                papers: ['刘江峰,张冉,张君冬,裴雷.以生成式人工智能赋能思想史计算研究：模型构建与应用探索[J].图书馆杂志,2025,44(03):113-127.DOI:10.13663/j.cnki.lj.2025.03.010.', '刘江峰,张冉,王东波,裴雷.追溯科学融合的历史轨迹：AIGC赋能交叉科学测度研究[J].图书情报工作,2024,68(07):137-150.DOI:10.13266/j.issn.0252-3116.2024.07.013.'],
                // description: '深入学习信息科学和数据分析，掌握用户行为研究和产品优化方法',
                relevance: '直接学习产品设计，掌握了用户研究、原型设计等核心技能'
            },
            {
                period: '2024.08-2025.12',
                school: 'UC Berkeley',
                degree: '交换生',
                major: 'Information Science',
                // gpa: '3.9/4.0',
                // ranking: 'Top 10%',
                // honors: ['Dean\'s List', 'International Student Excellence Award'],
                learningPoints: [
                    '深入学习数据科学和用户体验设计方法论',
                    '参与跨文化学术交流，提升国际化视野',
                    '完成多个实践项目，积累实战经验'
                ],
                relevance: 'Introduction to User Experience Design\nPrinciples & Techniques of Data Science\nSeminar and Research in social media'
            }
        ],

        // courses: [
        //     {
        //         name: '产品管理',
        //         description: '学习产品生命周期管理、需求分析、市场调研等核心技能',
        //         relevance: '直接相关课程，为产品经理职业发展提供理论基础'
        //     },
        //     {
        //         name: '用户体验设计',
        //         description: '掌握用户研究方法、交互设计、可用性测试等技能',
        //         relevance: '产品经理需要深度理解用户体验，这门课程提供了实践方法'
        //     },
        //     {
        //         name: '数据科学',
        //         description: '学习数据分析、机器学习、A/B测试等数据驱动决策方法',
        //         relevance: '现代产品经理需要数据驱动思维，这门课程培养了分析能力'
        //     }
        // ],
        awards: [
            {
                name: '优秀毕业生',
                year: '2024',
                description: '综合成绩排名前10%，获得优秀毕业生称号'
            },
            {
                name: '创新设计大赛一等奖',
                year: '2023',
                description: '团队项目获得校级创新设计大赛一等奖'
            }
        ]
    },

    internships: [
        {
            id: 'meituan',
            company: '美团',
            position: '用户产品实习生',
            period: '2025.01-2025.04',
            location: '上海',
            logo: '/images/logos/meituan.png',
            description: '负责美团大众点评写评价流程产品功能优化，提升用户体验和业务指标',
            workContent: {
                '为进一步降低写评成本、提升评价质量，强化POI下"智能+写评"能力建设，推出智能菜品标签方案': [
                    '为提升评价信息维度，针对菜品图片带标签率低（0.87%）问题，通过智能推荐形式降低用户操作成本，以美食下菜品为试点，建设POI-SKU的标签识别能力，上线后实现图片带标签率+2.16pp，带图片标签图片曝光点击率较大盘+4.63pp'
                ],
                '在探索语音表达形态背景下，兼容个人主页语音评价形态，推动写入、消费、记录侧多元评价形式整合': [
                    '确保历史评价支持语音展示和播放功能，设计语音播放逻辑并解决音频通道抢占问题，保证三侧用户体验的同步与流畅性'
                ],
                '细分场景下完成用户体验优化，围绕长文本及国际用户需求，完成写评页面输入功能优化与写流程多语言适配': [
                    '在原键入区域被压缩背景下，在写评页面完成通用文本输入改动，并针对长文案作者新增沉浸态文本输入功能，增强整体键入使用体验',
                    '顺应过境免签政策，完成写评价页面英文版适配，通过拆解不同类目下写评价页面的构成，设计并实现首屏英文展示及悬浮球切换能力'
                ],
                '探索评价完成页优化方向，提炼竞品激励与互动策略，构建可复用模块，旨在提升用户参与度与留存率': [
                    '针对评价完成页功能单一、营销感过重的问题，深入分析多类竞品UGC完成页的正反馈机制，提出个性化动画、评价分享卡片、多样化精神激励、物质激励注重玩法渗透等迭代方向，探索完成页新形态'
                ]
            },
            skills: ['产品设计', '数据分析', '用户研究', '项目管理']
        },
        {
            id: 'xiaomi',
            company: '小米',
            position: '策略产品实习生',
            period: '2024.07-2024.09',
            location: '南京',
            logo: '/images/logos/xiaomi.png',
            description: '负责小米软件中心游戏专区策略制定，分析市场趋势和用户需求',
            workContent: {
                '  为进一步提高轻游戏专区分发效率，满足用户的个性化需求，新增个性化召回实验策略，完善专区推荐策略建设': [
                    '推出个性化召回实验策略，基于用户过去30天游戏标签tag推荐相同标签游戏，关注人均曝光、人均分发、人均广告arpu影响数据，通过机器打分和CTR倒排结合方式进行游戏排序并推荐，实现人均曝光提升2.23%，人均分发提升7.45%'
                ],
                '  专区完成基础建设后亟待扩量，通过遍历应用商店内机会点，确认“人群+落地场景”，为轻游戏专区扩场景提升曝光': [
                    '协助计算入口预估收益和损失，通过在竞品详情页、搜索场景、用户调整tab权重及首页热词卡片等扩场景，为专区增加曝光和分发，实现全体用户广覆盖及休闲游戏偏好人群重点触达，实现从7-9月专区日活由4.8W增加至5.9W，上涨23% '
                ],
                '针对专区低次留问题，通过拆解优质竞品，新增双重复玩路径，有效提升专区游戏次留': [
                    '通过提炼优质竞品《233乐园》成功因素及创新玩法，建设轻游戏专区复玩路径，实验下专区游戏次留+6.35%；并为专区提出打造游戏社交圈、虚拟化游戏、站外推广流量引入的迭代方向'
                ]
            },
            skills: ['市场分析', '策略制定', '商业分析', '用户调研']
        },
        {
            id: 'xueda',
            company: '学大教育',
            position: 'B端产品实习生',
            period: '2024.02-2024.05',
            location: '北京',
            logo: '/images/logos/xueda.png',
            description: '负责学大教育B端产品功能设计，服务教师和机构用户',
            workContent: {
                '为学大教育内部业务人员打造移动端外呼平台，深入业务人员跟进sop流程，优化跟进效率工具，提升流程执行力': [
                    '负责系统移动端外呼功能的需求分析与产品设计，周均使用人数达 330 人；优化了线索/客户模块的"待跟进"与"定位"功能，优化后周均跟进人数提高约 6%'
                ],
                '日常进行功能及交互全局性盘点，对问题模块进行反馈并跟进': [
                    '进行了商机模块、试听排课、上门列表的验收，协调测试开发人员进行功能修正，确保各模块顺利上线'
                ]
            },
            skills: ['B端产品设计', '流程优化', '数据可视化', '业务理解']
        }
    ],

    projects: [
        {
            id: 'thinker-model',
            title: 'Thinkers AI',
            description: '核心目标：本项目的根本目标是解决思想史研究中，传统计算方法（如自然语言理解NLP）遇到的瓶颈问题——即模型训练成本高、泛化效果差、解读困难。我们计划利用当前最前沿的生成式人工智能（AIGC）技术，特别是大语言模型（LLM），来革新思想史料的计算化处理方式。产品定位：我们要打造一个名为“思想家模型 (Thinkers Model)”的垂直领域大模型。它并非一个通用的聊天机器人，而是一个专注于中国思想史领域的深度知识服务平台。其核心价值在于，能够高效、准确地处理和解读海量思想史文献（以《中国思想家评传》为起点），打破专业知识壁垒，为学术研究和文化传播提供全新的、交互式的解决方案。',
            slogan: 'AI赋能思想史研究，让传统文化焕发新活力',
            image: '/images/projects/thinker-model.jpg',
            features: [
                '专题知识问答',
                '思想流变分析',
                '跨文化思想对比研究',
                '古籍文本处理与活化'
            ],
            implementationPath: `
            在整个项目过程中，进行分阶段、有策略地推进该平台的构建与优化：

1. **第一阶段：数据基础与模型选型**

- 数据源确立： 选择《中国思想家评传》系列丛书作为核心数据源。这是一套权威、系统、内容丰富的学术专著，为模型训练提供了高质量的垂直领域语料。

- 基准模型选型： 选用业界领先且对中文支持友好的“百川大模型 (Baichuan2-7B-chat)”作为我们的技术基座。这使得我们不必从零开始，可以站在巨人的肩膀上进行领域化定制。

2. **第二阶段：三步走的模型构建与优化**

- 领域预训练 (Domain Pre-training)： 首先，用我们处理好的《评传》正文文本对基准模型进行“继续预训练”，使其初步吸收思想史领域的语言风格和专业知识。

- 有监督微调 (Supervised Fine-tuning)： 创新性地结合ChatGPT与提示工程，构建高质量的领域指令问答数据集。利用该数据集对模型进行指令微调，教会模型如何理解并回答思想史相关问题。此阶段，我们还设计了数据清洗和相似问答对合并的策略，以提升数据质量。

- 直接偏好优化 (DPO)： 替代传统复杂的RLHF，我们采用更高效的DPO方法。通过构建“一问多答”数据集，并借助ChatGPT辅助和人工校准进行偏好标注，直接优化模型，使其生成的内容更符合人类专家的偏好和认知。

3. **第三阶段：评估、迭代与未来展望**

- 效果评估： 采用机器自动评估（BLEU, ROUGE）和专家人工评估相结合的方式，与ChatGLM、Qwen等主流模型进行横向对比，确保我们的“思想家模型”在垂直领域具有显著优势。

- 持续迭代： 后续将不断扩充语料库、引入多模态信息（如图片、音视频）、优化用户交互体验，并持续关注模型伦理问题，确保内容的公正客观。
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Thinkers-AI',
            githubUrl: 'https://github.com/ivyzhang1912/Thinkers-AI'
        },
        {
            id: 'tech-policy',
            title: '0-1"科政明"科技政策推送平台',
            description: '该项目旨在解决科技政策信息爆炸式增长带来的信息利用效率低、阅读成本高和查询难度大等问题。项目通过收集全国各省市及重要城市科技部发布的逾三万条政策数据，开发一个科技政策数据移动应用平台（小程序）。平台利用自然语言处理（NLP）和深度学习技术，实现科技政策的自动化摘要生成与个性化精准推送，旨在帮助用户（特别是企业）高效、准确地获取并理解相关政策，从而提升政策的传播与执行效率。',
            slogan: '让政策信息触手可及，助力企业精准决策',
            image: '/images/projects/tech-policy.jpg',
            features: [
                '科技政策个性化精准推送',
                '科技政策摘要自动生成',
                '科技政策数据库搜索与查询',
                '用户个人信息管理'
            ],
            implementationPath: `
1. **需求调研与项目确立** 项目首先进行了市场需求调研，分析发现在科技政策信息领域，用户普遍面临信息过载、利用效率低、查询难度大等痛点。通过对比市面上已有的“政策快报”、“政策通”等产品，发现它们存在缺少内容摘要、无个性化推送、部分功能收费或含有广告等缺陷。基于此，项目确立了核心目标：开发一个完全免费、无广告的移动平台，利用自动化技术为用户提供精准的个性化政策推送和精炼的内容摘要，以解决现有市场痛点，提升用户获取和理解政策的效率。

2. **技术架构与核心算法** 目的技术实现主要基于“小程序前端-Web服务器后端-数据库/模型”三层架构。

- 前端实现：使用微信开发者工具，通过 WXML、WXSS 和 JavaScript 语言构建小程序的图形用户界面和交互逻辑。

- 后端实现：采用 Python 语言和 Flask Web应用框架搭建服务器，负责处理前端请求，并作为小程序与数据库及算法模型之间数据交互的桥梁。

- 数据与算法：

    - 数据处理与存储：爬取各省市科技部官网的3万余条政策数据，经过数据清洗、人工标注、关键词提取（TF-IDF算法）和摘要预生成后，存入 MySQL 关系型数据库。
    - 摘要生成技术：采用基于深度学习的生成式摘要模型（参考 Text-Summarizer-Pytorch-Chinese 模型），该模型基于指针生成网络（pointer-generator network），能够理解文本并生成流畅、自然的摘要内容，而非简单摘取原文。
    - 精准推送技术：采用基于内容的推荐算法。通过 TF-IDF 算法提取每篇政策的关键词，并根据用户的浏览历史构建一个动态更新的“用户偏好关键词列表”。推送时，系统会计算待推送政策与用户偏好关键词的拟合度，并将拟合度最高的政策推荐给用户。同时，为避免偏好过度收敛，该偏好列表会引入衰减系数进行定期更新。
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Tech_policy_platform',
            githubUrl: 'https://github.com/ivyzhang1912/Tech_policy_platform'
        },
        {
            id: 'barrage-analysis',
            title: '弹幕情感分析系统',
            description: '该用户研究通过分析B站“医学博士”系列高播放量视频的弹幕数据（数量波动、情感倾向、内容），揭示显著提升用户参与度和满意度的关键内容特征',
            slogan: '读懂弹幕，助力内容创作更精准',
            image: '/images/projects/barrage-analysis.jpg',
            features: [
                '弹幕情感分析',
                '情感趋势可视化',
                '视频内容特征',
                '内容优化建议'
            ],
            implementationPath: `
**核心痛点与用户需求 (User Pain Points & Needs)**:
本项研究（用户研究）聚焦于当前医学科普视频领域普遍存在的问题：部分视频过度娱乐化、内容说服力不足或难以吸引并留住观众。研究发现，用户对医学科普内容的需求是多元化的，不仅仅是获取知识（有用性），还包括娱乐放松（娱乐性）、情感共鸣（情感价值）以及互动交流（社会交往）。现有科普视频往往未能很好地平衡和满足这些复合需求。

**市场机会与产品/内容策略方向 (Market Opportunity & Strategy Direction)**:
随着新媒体发展和用户健康意识提升，高质量、高吸引力的医学科普视频市场需求巨大。B站等平台的弹幕数据提供了一个独特的、实时的窗口，可以直接观察用户在观看过程中的即时反应（关注点、情绪、疑问），这是传统问卷或访谈难以企及的深度用户洞察。
基于此用户研究，我们应制定内容策略，打造或优化医学科普视频产品，使其不仅传递准确的医学知识，更能通过娱乐性、互动性和情感共鸣，有效吸引、留住并影响目标受众。

**价值主张 (Value Proposition)**:

- 对内容创作者/平台： 提供基于用户真实反馈的洞察，指导内容创作与优化，提升视频的播放量、完播率、互动率和用户满意度，塑造专业且受欢迎的品牌形象。

- 对观众/用户： 提供既权威可信，又生动有趣、易于理解和记忆的医学科普内容，满足其多元需求，提升健康素养，并获得愉悦的观看和互动体验。

**主要功能/内容特征洞察 (Key Features/Content Insights from User Research)**:

这份用户研究通过分析B站“医学博士”系列高播放量视频的弹幕数据（数量波动、情感倾向、内容），揭示了以下能显著提升用户参与度和满意度的关键内容特征（可视为未来产品/内容设计的核心要素）：

**1. 娱乐与实用的平衡 (Balancing Entertainment & Utility)**:

- 用户洞察： 弹幕高发区往往对应视频中幽默风趣的表达、夸张表情或拟人化手法（娱乐性强），而纯知识讲解部分弹幕量较低（有用性强但单调）。

- 产品启示： 成功的科普视频需要巧妙融合娱乐元素与专业知识。不能为了科普而枯燥，也不能为了娱乐而牺牲严谨。需探索更佳的结合方式，如用生动案例解释复杂原理。

**2. 动态视觉呈现 (Dynamic Visual Presentation)**:

- 用户洞察： 弹幕低频区常对应静态图片或文字截图，而高频区多为动态画面。

- 产品启示： 视觉呈现应尽可能动态化，避免长时间的静态画面。动画、实景演示、甚至创作者生动的肢体语言都有助于吸引用户注意力。

**3. 实时互动设计 (Real-time Interaction Design)**:

- 用户洞察： 创作者在视频中直接提问或引导分享经历时，弹幕互动性显著增强，满足用户的社会交往需求。

- 产品启示： 在视频脚本中应策略性地设计互动环节（如设问、投票提示、鼓励分享），主动引导用户通过弹幕进行交流，营造“共时”的社区感。

**4. 情感共鸣与价值升华 (Emotional Resonance & Value Sublimation)**:

- 用户洞察： 视频结尾部分若能引发思考、传递正确价值观或揭示社会现象（如“水健康”视频结尾），常能引起用户强烈共鸣，并激发二次传播。

- 产品启示： 内容不仅要有趣、有用，还要有“点睛之笔”，在结尾引发用户的情感共鸣或价值认同，提升内容的记忆度和影响力。

**策略建议 (Strategic Recommendations Based on Research)**:

基于以上用户研究洞察，为提升医学科普视频的传播效果和用户满意度，建议采取以下策略：
    
**1. 内容风格策略**： 采用“非正式教程风格”，即保持医学内容的严谨性和权威性（例如，创作者身份认证、引用可靠来源），同时融入幽默、叙事（讲故事）元素，使用生动、接地气的语言，降低理解门槛，增强趣味性。

**2. 表现形式策略**： 注重视觉元素的动态化和多样性。合理使用背景音乐、字幕，必要时可采用动画等形式辅助解释，但需注意避免过度花哨干扰核心信息传递。平衡好视觉效果与信息传达的主次关系。

**3. 互动沟通策略**： 将互动视为内容的一部分。在视频中主动引导用户参与（提问、发起讨论），并积极回应用户反馈（包括弹幕和评论），营造双向沟通的社区氛围，将观众从被动接收者转变为主动参与者和传播者。

**4. 价值引导策略**： 选题应贴近用户关切，内容制作需审慎考虑潜在争议点，并主动传播正确的科学观念和价值观。通过共情和价值认同，深化内容的传播效果，促使用户自发分享。

**5. （持续优化）数据驱动策略**： 将弹幕分析常态化。利用文本挖掘技术持续监测弹幕的数量、情感、主题随时间（视频进度条）的变化，定期复盘，将用户反馈数据作为内容迭代优化的重要依据。
            `,
            demoUrl: 'https://github.com/ivyzhang1912/Bullet-Screen-Analysis',
            githubUrl: 'https://github.com/ivyzhang1912/Bullet-Screen-Analysis'
        }
    ],

    bookReviews: [
        {
            title: '幕后产品',
            author: '王诗沐',
            cover: '/images/books/muhouchanping.jpg',
            rating: 5,
            review: '这本书让我对“幕后产品思维”有了更系统的认知。作者不仅拆解了产品经理在战略、执行、协同等维度的核心职责，也强调了从价值洞察到方案落地的全链路思考方式。尤其是书中对需求挖掘、用户增长、MVP验证与迭代机制的解析，让我更深刻理解企业打造突破性产品背后的底层逻辑——洞察趋势、识别核心用户价值，并以实验与复盘驱动增长。',
            keyInsights: [
                '真正的用户洞察来自问题定义：产品人不仅要看到用户“说什么”，更要理解他们“为什么这样想、这样做”，洞察需求背后的动机与场景。',
                '数据和直觉是双引擎：产品判断不能只依赖单一维度——数据是验证工具，而用户反馈、业务趋势与行业经验同样关键。',
                '快速试错、持续迭代是增长根基：通过小步快跑和灰度实验，快速测试假设、缩小试错成本，在不确定中找到确定性。',
                '产品最终要对商业负责：功能不是目的，增长、留存、效率提升与商业化能力，才是衡量产品成功的核心指标。'
            ]
        },
        {
            title: '结构思考力',
            author: '李忠秋',
            cover: '/images/books/jiegousikaoli.jpg',
            rating: 5,
            review: '《结构思考力》强调通过结构化的方式来分析问题、组织信息与表达观点。阅读过程中我更加意识到，清晰的思维结构不仅能提升沟通效率，也是产品判断力与方案质量的底层能力。结构化不是模板，而是一种让信息更清晰、逻辑更严密的工作方式。',
            keyInsights: [
                '先结论、后分解：通过“先说结果、再说明理由”的方式，帮助信息呈现更具说服力、逻辑更清晰。',
                '拆解问题、重构结构：任何复杂问题都能通过“拆分＋归类＋重构”的方式让思考更系统、解决更有方向。 ',
                '把思维显性化、表达形象化：结构思考不仅是思考工具，更是让隐性思维转为可说、可呈现的结构，从而增强表达力。',
                '应用与场景化：结构化思维并非停留在方法层面，而是要结合具体场域（工作、汇报、写作）展开训练，并复制到日常实践中。'
            ]
        },
        {
            title: 'ASK YOUR DEVELOPER',
            author: 'Jeff Lawson',
            cover: '/images/books/askyourdeveloper.jpeg',
            rating: 4,
            review: '在数字化时代，每家公司其实都在变成“软件公司”。阅读过程中，我重新认识到产品经理/战略负责人与开发团队之间的关系：从“下达需求—等待交付”，转变为“共同定义问题—一起创造方案”。真正的价值在于让开发者成为业务价值、用户体验与战略增长的参与者，而不仅仅是技术产出者。',
            keyInsights: [
                '技术与业务共创文化是制胜关键：让开发团队贴近用户、贴近战略目标，并形成跨职能协作、共享语言的文化，才能释放整体组织的潜能。',
                '迭代与实验是竞争优势：传统“计划—执行”模式慢且僵化，现代优秀组织通过小规模实验、快速反馈、持续扩展来抢占市场机遇。',
                '构建核心能力，选择性外买：企业应聚焦于“真正差异化”的软件能力进行自主构建，其它可模块化、可服务化的部分可采用外部平台。']
        }
    ],

    resume: {
        chinese: '/files/resume-zh.pdf',
        english: '/files/resume-en.pdf'
    },

    footer: {
        statement: '本网站所展示的所有信息均为真实信息，包括教育背景、实习经历、项目经验等。如有任何疑问，欢迎联系我进行核实。',
        companyLogos: [
            {
                name: '美团',
                logo: '/images/logos/meituan.png',
                url: 'https://www.meituan.com'
            },
            {
                name: '小米',
                logo: '/images/logos/xiaomi.png',
                url: 'https://www.mi.com'
            },
            {
                name: '学大教育',
                logo: '/images/logos/xueda.png',
                url: 'https://www.xueda.com'
            }
        ],
        contact: {
            email: 'ranzhang1912@gmail.com',
            linkedin: 'www.linkedin.com/in/ivy-zhang-a6713a384',
            github: 'https://github.com/ivyzhang1912'
        }
    }
};


// 状态管理
interface DashboardState {
    config: WebsiteConfig;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
    config: defaultConfig
}));

